/**
 * The events grid's standard look (the prototype's #/events page, card A).
 * Draws the behavior half's state and calls its actions; holds no rule.
 */
import { partsOf, pieceRoot, usePlaced } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { onLink } from '../../ui/onLink';
import { EventCard } from '../events-row/EventCard';
import type { EventsGridRuntime } from './useEventsGrid';
import css from './EventsGrid.module.css';
import { contract } from './contract';

const part = partsOf(contract);

export function EventsGridView({ state, actions, options }: EventsGridRuntime) {
  // Placed on a custom page (web-studio-sites D4): no frame and no breadcrumb, the page is hers.
  const placed = usePlaced();
  const anyPhoto = state.events.some((e) => e.photo);
  const label = options.heading ?? 'Events';
  return (
    <section className={placed ? undefined : shared.wrap} aria-label={label} {...pieceRoot(contract)} {...part('section')}>
      {state.studioName && !placed && (
        <nav className={css.crumbs} aria-label="Breadcrumb" {...part('breadcrumb')}>
          <a href="/" onClick={onLink(actions.home)} {...part('breadcrumb-home')}>
            {state.studioName}
          </a>
          <span className={css.crumbSep} aria-hidden="true" {...part('breadcrumb-separator')}>
            /
          </span>
          <span className={css.crumbHere} aria-current="page" {...part('breadcrumb-here')}>
            Events
          </span>
        </nav>
      )}
      {options.heading && (
        <div className={`${shared.secHead} ${css.evPgHead}`} {...part('head')}>
          <h1 className={shared.h2} {...part('heading')}>
            {options.heading}
          </h1>
        </div>
      )}
      {state.status === 'ready' && state.events.length === 0 && options.emptyText && (
        <p className={css.empty} {...part('empty')}>
          {options.emptyText}
        </p>
      )}
      {state.events.length > 0 && (
        <ul className={css.evGrid} aria-label={label} {...part('list')}>
          {state.events.map((e) => (
            <EventCard
              key={e.id}
              event={e}
              timezone={state.timezone}
              showPhoto={options.showPhoto && anyPhoto}
              showSummary={options.showSummary}
              mark={state.marks[e.id]}
              onOpen={() => actions.open(e.id)}
              onOpenBooking={() => actions.openBooking(e.id)}
            />
          ))}
        </ul>
      )}
      {state.hasMore && (
        <div className={css.more} {...part('more')}>
          <button
            type="button"
            className={`${shared.btn} ${shared.btnOutline}`}
            onClick={actions.loadMore}
            disabled={state.loadingMore}
            {...part('load-more', state.loadingMore && 'disabled')}
          >
            {state.loadingMore ? 'Loading' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}
